import { ReactiveVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { LOCAL_STORAGE_KEYS } from '@fdc/constants/local-storage-keys.constants';
import { Dispatch, SetStateAction, useEffect } from 'react';

import { localStorageService } from '@contentservice/shared/services/storage/local-storage.service';

type LocalStorageKey = (typeof LOCAL_STORAGE_KEYS)[number];

/**
 * A custom hook that functions like `useState` but persists the state
 * to `localStorage`, allowing it to be retained across browser sessions.
 *
 * @param key The key to use when storing the value in localStorage.
 * @param reactiveVar The Apollo reactive variable to keep in sync.
 * @returns A stateful value, an updater function, and a clear function.
 */
export function useGlobalLocalStorageState<T>(
  key: LocalStorageKey,
  reactiveVar: ReactiveVar<T>
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const storedValue = useReactiveVar<T>(reactiveVar);

  useEffect(() => {
    const item = localStorageService.get(key);
    if (item) {
      let parsedItem;
      try {
        parsedItem = JSON.parse(item);
      } catch {
        return;
      }
      reactiveVar(parsedItem);
    }
  }, [key, reactiveVar]);

  const setValue: Dispatch<SetStateAction<T>> = (value) => {
    const valueToStore = value instanceof Function ? value(storedValue) : value;

    reactiveVar(valueToStore);
    localStorageService.set(key, JSON.stringify(valueToStore));
  };

  const clearValue = () => {
    localStorageService.remove(key);
  };

  return [storedValue, setValue, clearValue];
}

export default useGlobalLocalStorageState;
